import epsClient from 'enrollment-platform/api/eps.client'
import t from './actionTypes'
import { selectors as userSessionSelectors } from 'store/modules/user-session'
import dayjs from 'dayjs'

interface CurrentCompany {
  Id?: string
  CompanyName?: string
  BenefitsEffectiveDate?: string
  [key: string]: any
}

export interface Location {
  County: string
  StateProvince: string
  ZipCode: string
  CountyFipsCode: string
  RatingAreaNumeric: string
  StateAndRatingAreaNumeric: string
}

interface EpsLocationResponse {
  county: string
  state: string
  zipCode: string
  countyFipsCode: string
  ratingAreaNumeric: string
}

/**
 * Locations API Migration:
 *
 * This module has been migrated from the old AWS API Gateway (countiesGet)
 * to the new secure EPS search locations API. The new API:
 * - Accepts year and zipCodes parameters
 * - Returns location data in a different format that gets transformed
 * - Uses the EPS client for authentication and base URL configuration
 * - Handles multiple response formats for compatibility
 */

export const loadLocations = (locations: Location[], postalCode: string) => ({
  type: t.LOAD_LOCATIONS,
  payload: { locations, postalCode },
})

export const resetLocations = (postalCode: string) => ({
  type: t.RESET_LOCATIONS,
  payload: { postalCode },
})

export const restoreSelection = (postalCode: string, county: string) => ({
  type: t.RESTORE_SELECTION,
  payload: { postalCode, county },
})

export const clearPendingRestoration = (postalCode: string) => ({
  type: t.CLEAR_PENDING_RESTORATION,
  payload: { postalCode },
})

// Get the appropriate year based on context:
// 1. Get year from person's benefit effective date (form values or saved person data)
// 2. OR client's max benefit year if not available
// 3. OR max location mapping year if not available
const getAppropriateYear = async (getState: any) => {
  const currentYear = new Date().getFullYear()

  try {
    // Get current company from user session
    const currentCompany: CurrentCompany =
      userSessionSelectors.getCurrentCompany(getState())

    if (currentCompany && currentCompany.Id) {
      // First, check if there's a benefit effective date in the current form values
      // This takes priority over the saved person data
      const addEmployeeForm = getState().form?.addEmployee?.values
      const editEmployeeForm = getState().form?.editEmployee?.values
      const currentFormValues = addEmployeeForm || editEmployeeForm

      if (currentFormValues && currentFormValues.BenefitsEffectiveDate) {
        // Extract year from benefit effective date using dayjs to avoid timezone issues
        const benefitYear = dayjs(
          currentFormValues.BenefitsEffectiveDate,
          'YYYY-MM-DD'
        ).year()
        return benefitYear
      }

      // If no person benefit effective date found, try client's max benefit year
      try {
        const clientPublicKey = currentCompany.Id
        const maxBenefitYearResponse = await epsClient.get(
          `/customers/${clientPublicKey}/configuration/enrollment/max-benefit-year`
        )

        if (
          maxBenefitYearResponse.data &&
          maxBenefitYearResponse.data.maxYear
        ) {
          return maxBenefitYearResponse.data.maxYear
        }
      } catch (error) {
        console.warn('Could not get client max benefit year:', error)
      }

      // If client max benefit year not available, try max location mapping year
      try {
        const maxLocationYearResponse = await epsClient.get(
          '/locations/max-year'
        )

        if (
          maxLocationYearResponse.data &&
          maxLocationYearResponse.data.maxYear
        ) {
          return maxLocationYearResponse.data.maxYear
        }
      } catch (error) {
        console.warn('Could not get max location mapping year:', error)
      }

      // Final fallback to current year
      return currentYear
    }
  } catch (error) {
    console.warn('Could not determine context for year selection:', error)
  }

  // Default fallback to current year
  return currentYear
}

export const getLocations =
  (postalCode: string | null | undefined) => async (dispatch, getState) => {
    if (postalCode?.length === 5) {
      try {
        const year = await getAppropriateYear(getState)

        // Use the new EPS locations search API with GET method and query parameters
        const response = await epsClient.get('/locations/search', {
          params: {
            year,
            zipCodes: postalCode,
          },
        })

        // Transform the EPS API response to match the expected format
        // The API returns an array of location objects with lowercase properties
        const locations: EpsLocationResponse[] = response.data || []

        // Transform the locations to match the expected format
        const transformedLocations: Location[] = locations.map((location) => ({
          County: location.county,
          StateProvince: location.state,
          ZipCode: location.zipCode,
          CountyFipsCode: location.countyFipsCode,
          RatingAreaNumeric: location.ratingAreaNumeric,
          StateAndRatingAreaNumeric: `${location.state}-${location.ratingAreaNumeric}`,
        }))

        dispatch(loadLocations(transformedLocations, postalCode))
      } catch (error) {
        console.error('Error fetching locations from EPS API:', error)
        // Return empty array on error to maintain compatibility
        dispatch(loadLocations([], postalCode))
      }
    }
  }

/**
 * Helper function to check if a county selection is still valid in the new locations list
 */
const isSelectionValid = (
  county: string | null | undefined,
  locations: Location[]
): boolean => {
  if (!county || !locations || locations.length === 0) {
    return false
  }
  return locations.some((location) => location.County === county)
}

/**
 * Helper function to find the best matching county if exact match is not available
 */
const findBestMatch = (
  previousCounty: string | null | undefined,
  locations: Location[]
): Location | null => {
  if (!previousCounty || !locations || locations.length === 0) {
    return null
  }

  // Try exact match first
  const exactMatch = locations.find(
    (location) => location.County === previousCounty
  )
  if (exactMatch) {
    return exactMatch
  }

  // Try partial match (case insensitive) with null safety
  const partialMatch = locations.find((location) => {
    if (!location.County || !previousCounty) {
      return false
    }
    return (
      location.County.toLowerCase().includes(previousCounty.toLowerCase()) ||
      previousCounty.toLowerCase().includes(location.County.toLowerCase())
    )
  })
  if (partialMatch) {
    return partialMatch
  }

  return null
}

/**
 * Enhanced getLocations action that preserves the current county selection
 * when reloading locations due to benefit effective date changes
 */
export const getLocationsWithPreservation =
  (postalCode?: string | null, previousCounty?: string | null) =>
  async (dispatch, getState) => {
    if (postalCode?.length === 5) {
      try {
        const year = await getAppropriateYear(getState)

        // Use the new EPS locations search API with GET method and query parameters
        const response = await epsClient.get('/locations/search', {
          params: {
            year,
            zipCodes: postalCode,
          },
        })

        // Transform the EPS API response to match the expected format
        const locations: EpsLocationResponse[] = response.data || []
        const transformedLocations: Location[] = locations.map((location) => ({
          County: location.county,
          StateProvince: location.state,
          ZipCode: location.zipCode,
          CountyFipsCode: location.countyFipsCode,
          RatingAreaNumeric: location.ratingAreaNumeric,
          StateAndRatingAreaNumeric: `${location.state}-${location.ratingAreaNumeric}`,
        }))

        // Load the new locations
        dispatch(loadLocations(transformedLocations, postalCode))

        // Attempt to preserve the previous selection
        if (previousCounty) {
          if (isSelectionValid(previousCounty, transformedLocations)) {
            // Selection is still valid, restore it
            dispatch(restoreSelection(postalCode, previousCounty))
          } else {
            // Try to find a best match
            const bestMatch = findBestMatch(
              previousCounty,
              transformedLocations
            )
            if (bestMatch) {
              dispatch(restoreSelection(postalCode, bestMatch.County))
              // Note: We'll add notification logic in the component level
            }
            // If no match found, selection will remain cleared
          }
        }
      } catch (error) {
        console.error('Error fetching locations from EPS API:', error)
        // Return empty array on error to maintain compatibility
        dispatch(loadLocations([], postalCode))
      }
    }
  }
